<script setup lang="ts">
import type { UspTypeFragment } from '#graphql-operations'

import { AppLink } from '#components'

const props = withDefaults(defineProps<{
  usps: UspTypeFragment
  horizontal?: boolean
  placement?: string
  slider?: boolean
}>(), {
  horizontal: () => false,
  placement: () => '',
  slider: () => false,
})

const { loadIcon } = useDynamicIcons()

const activeIndex = ref(0)
let interval: NodeJS.Timeout | null = null

const placementClass = computed(() => {
  return props.placement ? 'usps--' + props.placement : undefined
})

onMounted(() => {
  if (props.usps?.Usp?.length && !props.slider) {
    interval = setInterval(() => {
      activeIndex.value++
      if (activeIndex.value >= props.usps.Usp.length) {
        activeIndex.value = 0
      }
    }, 5000)
  }
})

onUnmounted(() => {
  if (interval) {
    clearInterval(interval)
    interval = null
  }
})

const getElement = (url: string | null) => {
  return url ? AppLink : 'span'
}
</script>

<template>
  <div
    v-if="usps?.Usp?.length"
    class="usps"
    :class="[placementClass, { 'usps--slider': slider }]"
  >
    <div
      v-if="usps.Title"
      class="usps__title"
    >
      {{ usps.Title }}
    </div>

    <template v-if="!slider">
      <ul
        class="usps__list"
        :class="{
          'usps__list--horizontal': horizontal,
          'usps__list--vertical': !horizontal,
        }"
      >
        <li
          v-for="(usp, index) in usps.Usp"
          :key="index"
          class="usps__item"
          :class="{
            'usps__item--active': index === activeIndex,
            'usps__item--with-icon': usp.Icon,
          }"
        >
          <component
            :is="getElement(usp.Link)"
            :to="usp.Link"
            class="usps__link"
          >
            <NuxtIcon
              v-if="usp.Icon"
              :name="loadIcon(usp.Icon)"
            />

            <span class="usps__label">{{ usp.Label }}</span>
          </component>

          <div class="usps__description">
            {{ usp.Description }}
          </div>
        </li>
      </ul>
    </template>
    <template v-else>
      <UiCarousel
        class="usps-slider__list"
        :class="{
          /* Non responsive & non modern settings */
          'ui-carousel--center': false,
          'ui-carousel--free-scroll': true,
          'ui-carousel--navigation': false,
          'ui-carousel--pagination': true,
          'ui-carousel--progress': false,
        }"
      >
        <template
          v-for="(usp, uspIndex) in usps.Usp"
          #[`slide-${uspIndex}`]
          :key="usp"
        >
          <div
            class="usps-slider__item"
            :class="{ 'usps-slider__item--with-icon': usp.Icon }"
          >
            <component
              :is="getElement(usp.Link)"
              :to="usp.Link"
              class="usps-slider__content"
              :class="{
                'ct-usps-slider__link': usp.Link,
              }"
            >
              <NuxtIcon
                v-if="usp.Icon"
                :name="loadIcon(usp.Icon)"
              />
              <span class="usps-slider__label">{{ usp.Label }}</span>
              <span class="usps-slider__description">{{ usp.Description }}</span>
            </component>
          </div>
        </template>
      </UiCarousel>
    </template>
  </div>
</template>

<style>
@import url("~layers/components/App/Usps.css");
</style>
